'use client';

import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Input, Button } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { OtpVerification } from './OtpVertification';
import { notify } from '@utils/notify';
import authApi from '@apis/auth';

// Validation schema for registration
const registerSchema = Yup.object().shape({
  email: Yup.string().email('Email không hợp lệ').required('Email là bắt buộc'),
  password: Yup.string()
    .min(7, 'Mật khẩu phải có nhiều hơn 6 ký tự')
    .required('Mật khẩu là bắt buộc'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Mật khẩu không khớp')
    .required('Xác nhận mật khẩu là bắt buộc'),
});

export function RegisterForm({ onClose }: { onClose: () => void }) {
  const [registrationStep, setRegistrationStep] = useState(1); // 1: Form, 2: OTP Verification
  const [userEmail, setUserEmail] = useState('');

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: registerSchema,
    onSubmit: async (values) => {
      try {
        const res = await authApi.register({
          email: values.email?.trim(),
          password: values.password?.trim(),
        });

        setUserEmail(res.data.email);
        setRegistrationStep(2);
      } catch (error) {
        setRegistrationStep(1);
        notify.error(error?.message);
      }
    },
  });

  const handleVerificationSuccess = () => {
    onClose();
  };

  const handleBackToForm = () => {
    setRegistrationStep(1);
  };

  // Step 1: Registration Form
  if (registrationStep === 1) {
    return (
      <div className="py-2">
        <form onSubmit={formik.handleSubmit} className="grid gap-4">
          <div>
            <div className="text-sm font-medium mb-1">Email</div>
            <Input
              id="email"
              name="email"
              className="!h-12"
              prefix={<MailOutlined />}
              placeholder="your@email.com"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              status={formik.touched.email && formik.errors.email ? 'error' : ''}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="text-red-500 text-sm mt-1">{formik.errors.email}</div>
            )}
          </div>

          <div>
            <div className="text-sm font-medium mb-1">Mật khẩu</div>
            <Input.Password
              id="password"
              name="password"
              placeholder="Nhập mật khẩu"
              className="!h-12"
              prefix={<LockOutlined />}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              status={formik.touched.password && formik.errors.password ? 'error' : ''}
            />
            {formik.touched.password && formik.errors.password && (
              <div className="text-red-500 text-sm mt-1">{formik.errors.password}</div>
            )}
          </div>

          <div>
            <div className="text-sm font-medium mb-1">Xác nhận mật khẩu</div>
            <Input.Password
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Nhập mật khẩu"
              className="!h-12"
              prefix={<LockOutlined />}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              status={
                formik.touched.confirmPassword && formik.errors.confirmPassword ? 'error' : ''
              }
            />
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <div className="text-red-500 text-sm mt-1">{formik.errors.confirmPassword}</div>
            )}
          </div>

          <div className="mt-6">
            <Button
              type="primary"
              htmlType="submit"
              className="!h-12"
              loading={formik.isSubmitting}
              block
            >
              {formik.isSubmitting ? 'Đang xử lý...' : 'Tiếp tục'}
            </Button>
          </div>
        </form>

        {/* <Divider plain>Hoặc đăng ký với</Divider>

        <div className="grid grid-cols-2 gap-4">
          <Button icon={<GoogleOutlined />} onClick={handleGoogleRegister} block>
            Google
          </Button>
          <Button icon={<FacebookOutlined />} onClick={handleFacebookRegister} block>
            Facebook
          </Button>
        </div> */}
      </div>
    );
  }

  // Step 2: OTP Verification
  return (
    <OtpVerification
      email={userEmail}
      onVerificationSuccess={handleVerificationSuccess}
      onBack={handleBackToForm}
    />
  );
}
